import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Mic, Square, Loader2, Play, Volume2, RotateCcw, Sparkles, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const MAX_RECORD_SEC = 300; // 5 minutes

const OralPracticePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [recordingState, setRecordingState] = useState('idle'); // idle | recording | processing
  const [elapsed, setElapsed] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [attemptHistory, setAttemptHistory] = useState([]);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTsRef = useRef(0);
  const tickRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.oralListActive();
        setPassages(data.passages || []);
        if ((data.passages || []).length > 0) setSelected(data.passages[0]);
      } catch (e) {
        toast.error('Failed to load passages');
      } finally {
        setLoading(false);
      }
      try {
        const h = await api.oralMyAttempts();
        setAttemptHistory(h.attempts || []);
      } catch (e) {}
    })();
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line
  }, []);

  const resetAttempt = () => {
    setLastResult(null);
    setRecordedBlob(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setElapsed(0);
  };

  const startRecording = async () => {
    if (!selected) return;
    resetAttempt();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setRecordedBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        streamRef.current && streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };
      mr.start();
      startTsRef.current = Date.now();
      setRecordingState('recording');
      setElapsed(0);
      tickRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startTsRef.current) / 1000);
        setElapsed(secs);
        if (secs >= MAX_RECORD_SEC) stopRecording();
      }, 250);
    } catch (e) {
      toast.error('Microphone access denied. Please allow the mic and try again.');
    }
  };

  const stopRecording = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecordingState('idle');
  };

  const submitAttempt = async () => {
    if (!recordedBlob || !selected) return;
    setRecordingState('processing');
    try {
      const dur = elapsed || Math.round(recordedBlob.size / 16000); // rough fallback
      const result = await api.oralSubmitAttempt(selected.id, recordedBlob, dur);
      setLastResult(result);
      toast.success(`Score: ${result.overall_score}/100`);
      try {
        const h = await api.oralMyAttempts();
        setAttemptHistory(h.attempts || []);
      } catch (e) {}
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grading failed. Please retry.');
    } finally {
      setRecordingState('idle');
    }
  };

  const formatSec = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const scoreColor = (s) => {
    if (s >= 80) return 'text-emerald-600';
    if (s >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };
  const scoreBg = (s) => {
    if (s >= 80) return 'from-emerald-500 to-teal-500';
    if (s >= 60) return 'from-amber-500 to-orange-500';
    return 'from-rose-500 to-red-500';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="rounded-full" data-testid="oral-back-button">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-heading font-bold text-emerald-600" data-testid="oral-page-title">Oral Reading Practice</h1>
                <p className="text-xs text-slate-500">Listen · Read aloud · Get instant AI feedback</p>
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-12 h-12 object-contain"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="text-center py-16" data-testid="oral-loading">
            <Loader2 className="w-16 h-16 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-lg text-slate-600">Loading passages...</p>
          </div>
        ) : passages.length === 0 ? (
          <Card className="p-10 rounded-3xl border-2 border-slate-200 text-center max-w-2xl mx-auto" data-testid="oral-empty">
            <BookOpen className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-slate-800 mb-2">No oral passages yet</h2>
            <p className="text-slate-600">Ask your admin to upload passages so you can practise.</p>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left column: passage picker + PDF viewer */}
            <div className="lg:col-span-2 space-y-6">
              <div className="grid sm:grid-cols-3 gap-3">
                {passages.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setSelected(p); resetAttempt(); }}
                    className={`text-left rounded-2xl p-4 border-2 transition ${
                      selected?.id === p.id
                        ? 'border-emerald-500 bg-emerald-50 shadow'
                        : 'border-slate-200 bg-white hover:border-emerald-300'
                    }`}
                    data-testid={`oral-passage-${p.id}`}
                  >
                    <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">{p.level}</div>
                    <div className="text-sm font-bold text-slate-800 leading-snug line-clamp-2">{p.title}</div>
                  </button>
                ))}
              </div>

              {selected && (
                <Card className="rounded-3xl border-2 border-slate-200 overflow-hidden" data-testid="oral-passage-viewer">
                  <div className="p-4 bg-emerald-50 border-b-2 border-emerald-100 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Passage · {selected.level}</div>
                      <h2 className="text-lg font-heading font-bold text-slate-800">{selected.title}</h2>
                    </div>
                    <audio
                      key={`ref-${selected.id}`}
                      src={api.oralAudioUrl(selected.id)}
                      controls
                      className="max-w-full"
                      data-testid="oral-reference-audio"
                    />
                  </div>
                  <div className="bg-slate-100">
                    <iframe
                      key={`pdf-${selected.id}`}
                      title="Oral Passage PDF"
                      src={api.oralPdfUrl(selected.id)}
                      className="w-full h-[520px] bg-white"
                      data-testid="oral-passage-pdf"
                    />
                  </div>
                  <div className="p-4 border-t-2 border-slate-100 bg-white">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Passage text</div>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="oral-passage-text">
                      {selected.passage_text}
                    </p>
                  </div>
                </Card>
              )}
            </div>

            {/* Right column: record + result */}
            <div className="space-y-6">
              <Card className="rounded-3xl p-6 border-2 border-slate-200 bg-white" data-testid="oral-record-card">
                <h3 className="text-lg font-heading font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Mic className="w-5 h-5 text-emerald-600" strokeWidth={2.5} />
                  Record your reading
                </h3>

                {recordingState === 'recording' ? (
                  <>
                    <div className="text-center py-6">
                      <div className="text-5xl font-heading font-black text-rose-600 tabular-nums" data-testid="oral-elapsed">
                        {formatSec(elapsed)}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">Max {formatSec(MAX_RECORD_SEC)} · reading in progress</div>
                      <div className="mt-4 flex justify-center">
                        <span className="inline-flex h-3 w-3 rounded-full bg-rose-500 animate-pulse mr-2 mt-1.5"></span>
                        <span className="text-sm font-bold text-rose-700">Recording…</span>
                      </div>
                    </div>
                    <Button
                      onClick={stopRecording}
                      className="w-full rounded-full bg-rose-600 hover:bg-rose-700 text-white py-6 font-bold"
                      data-testid="oral-stop-button"
                    >
                      <Square className="w-5 h-5 mr-2" strokeWidth={3} />
                      Stop
                    </Button>
                  </>
                ) : recordingState === 'processing' ? (
                  <div className="text-center py-10" data-testid="oral-processing">
                    <Loader2 className="w-14 h-14 animate-spin text-emerald-500 mx-auto mb-3" />
                    <p className="text-sm font-bold text-slate-700">Prof.Scaly is listening…</p>
                    <p className="text-xs text-slate-500 mt-1">Transcribing & grading your reading</p>
                  </div>
                ) : recordedBlob ? (
                  <>
                    <div className="mb-3 text-xs text-slate-500 text-center">Duration: {formatSec(elapsed)}</div>
                    <audio src={previewUrl} controls className="w-full mb-4" data-testid="oral-playback" />
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={resetAttempt}
                        variant="outline"
                        className="rounded-full py-5 font-bold"
                        data-testid="oral-retry-button"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" strokeWidth={2.5} />
                        Redo
                      </Button>
                      <Button
                        onClick={submitAttempt}
                        className="rounded-full py-5 font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="oral-submit-button"
                      >
                        <Sparkles className="w-4 h-4 mr-1" strokeWidth={2.5} />
                        Grade me
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                      Listen to the reference audio, then tap <strong>Start</strong> and read the passage aloud
                      clearly. When done, tap <strong>Stop</strong> and we'll grade you instantly.
                    </p>
                    <Button
                      onClick={startRecording}
                      disabled={!selected}
                      className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 font-bold"
                      data-testid="oral-start-button"
                    >
                      <Mic className="w-5 h-5 mr-2" strokeWidth={3} />
                      Start recording
                    </Button>
                  </>
                )}
              </Card>

              {/* Recent history */}
              {attemptHistory.length > 0 && (
                <Card className="rounded-3xl p-5 border-2 border-slate-200 bg-white" data-testid="oral-history-card">
                  <h4 className="text-sm font-heading font-bold text-slate-800 mb-3">Your recent attempts</h4>
                  <div className="space-y-2 max-h-56 overflow-auto pr-1">
                    {attemptHistory.slice(0, 8).map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-xl px-3 py-2" data-testid={`oral-history-${a.id}`}>
                        <div className="min-w-0">
                          <div className="font-bold text-slate-800 truncate">{a.passage_title}</div>
                          <div className="text-slate-500">
                            {new Date(a.created_at).toLocaleString('en-SG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <div className={`text-lg font-heading font-black ${scoreColor(a.overall_score)}`}>{a.overall_score}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Full-width result */}
            {lastResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="lg:col-span-3"
                data-testid="oral-result-card"
              >
                <Card className="rounded-3xl border-2 border-emerald-300 bg-white overflow-hidden">
                  <div className={`p-6 bg-gradient-to-r ${scoreBg(lastResult.overall_score)} text-white`}>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-white/80">Overall reading score</div>
                        <div className="text-6xl font-heading font-black" data-testid="oral-overall-score">{lastResult.overall_score}<span className="text-2xl text-white/70">/100</span></div>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="bg-white/20 rounded-2xl px-4 py-3">
                          <div className="text-[10px] font-black uppercase tracking-wide text-white/80">Pronunciation</div>
                          <div className="text-2xl font-heading font-bold" data-testid="oral-score-pron">{lastResult.pronunciation_score}</div>
                        </div>
                        <div className="bg-white/20 rounded-2xl px-4 py-3">
                          <div className="text-[10px] font-black uppercase tracking-wide text-white/80">Fluency</div>
                          <div className="text-2xl font-heading font-bold" data-testid="oral-score-fluency">{lastResult.fluency_score}</div>
                        </div>
                        <div className="bg-white/20 rounded-2xl px-4 py-3">
                          <div className="text-[10px] font-black uppercase tracking-wide text-white/80">Pace</div>
                          <div className="text-2xl font-heading font-bold" data-testid="oral-score-pace">{lastResult.pace_score}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-heading font-bold text-slate-800 mb-2">Prof.Scaly's feedback</h4>
                      <p className="text-sm text-slate-700 leading-relaxed bg-emerald-50 border-2 border-emerald-100 rounded-2xl p-4" data-testid="oral-feedback">
                        {lastResult.overall_feedback}
                      </p>

                      <h4 className="text-sm font-heading font-bold text-slate-800 mt-5 mb-2">
                        Words to work on <span className="text-xs text-slate-500 font-medium">({(lastResult.mispronounced_words || []).length})</span>
                      </h4>
                      {(lastResult.mispronounced_words || []).length === 0 ? (
                        <p className="text-sm text-emerald-700 font-bold">🎉 No mispronunciations detected!</p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-auto pr-1" data-testid="oral-misspelled-list">
                          {(lastResult.mispronounced_words || []).map((m, idx) => (
                            <div key={idx} className="text-xs bg-rose-50 border border-rose-200 rounded-xl p-3">
                              <div className="flex items-baseline gap-2">
                                <span className="font-bold text-rose-700">{m.expected}</span>
                                <span className="text-slate-500">heard as</span>
                                <span className="italic text-slate-700">"{m.heard || '—'}"</span>
                              </div>
                              {m.tip && <div className="text-slate-600 mt-1">💡 {m.tip}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <h4 className="text-sm font-heading font-bold text-slate-800 mb-2">Colour-coded reading</h4>
                      <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-4 max-h-96 overflow-auto leading-loose text-base" data-testid="oral-colored-transcript">
                        {(lastResult.colored_transcript || []).length === 0 ? (
                          <p className="text-sm text-slate-500 italic">No transcript available.</p>
                        ) : (
                          (lastResult.colored_transcript || []).map((w, i) => {
                            const cls = w.status === 'correct'
                              ? 'text-emerald-700'
                              : w.status === 'mispronounced'
                              ? 'text-rose-700 underline decoration-2 decoration-rose-400'
                              : 'text-slate-400 line-through';
                            return (
                              <span key={i} className={`inline-block mr-1 font-medium ${cls}`}>
                                {w.word}
                              </span>
                            );
                          })
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-600 font-semibold">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Correct</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> Mispronounced</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" /> Missed</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default OralPracticePage;
